import { Dropdown } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import type { FunctionComponent, VoidFunctionComponent } from 'react';
import type { InjectedRouter } from 'react-router';
import { PACKAGE_INVENTORY, RAW_VULN_EDIT, SBOM_PORTAL } from '../../../shared/featureFlags/flagNames';
import { Action } from '../../../typings/permissions';
import { window } from '../../globals';
import { isFeatureEnabled, isQualityEnabled, isSecurityEnabled } from '../../helpers/features';
import { userHasPermissionForPolicyType, userHasPermissionToCreateOrgReports, userHasPermissionToCreateTeamReports } from '../../helpers/permissions';
import NavigationStore from '../../redux/navigation';
import BannerManager from '../BannerManager';
import FOSSALogo from '../generic/FOSSAInlineSVG/FOSSALogo';
import { Link, RouterLink } from '../generic/Link';
import OrgAlertBanner from '../generic/Banners/OrgAlertBanner';
import type { RouterProps } from '../../types';
import { PolicyType } from '../../types';
import AccountDropdown from './AccountDropdown';
import { getCategoryLabel, permittedIssueCategoriesToView } from '../../helpers/issuesV2';
import { Category } from '../../../shared/issuesV2';
import SupportDropdown from './SupportDropdown';
import { FeatureFlagRequired } from '../generic';
import SBOMPortalLinks from './SBOMPortalLinks';
import MenuItemLink from './MenuItemLink';
import { orgHasIssueOverviewEnabled } from '../issueOverview/accessControl';
import AnnouncementBanner from '../generic/Banners/AnnouncementBanner';
import { isFreeTier } from '../../../shared/subscriptions';
import { issueOverviewFeature } from '../settings/billing/features/consts';
import PaidFeatureGate from '../generic/Gates/PaidFeatureGate';
import PortalUrlOnlyGate from '../generic/Gates/PortalUrlOnlyGate';

// Only paths that start with /projects or /release-groups are considered.
// This is to avoid conflicts with other paths that contain /projects or /release-groups in them (e.g. /account/settings/projects/general).
const projectsPagePath = /^\/projects\/?/;
const releaseGroupsPagePath = /^\/release-groups\/?/;

const getDefaultPoliciesPage = () => {
  if (userHasPermissionForPolicyType(Action.View, PolicyType.LICENSING)) {
    return Category.Licensing;
  }
  if (
    isSecurityEnabled() &&
    userHasPermissionForPolicyType(Action.View, PolicyType.SECURITY)
  ) {
    return Category.Vulnerability;
  }
  if (
    isQualityEnabled() &&
    userHasPermissionForPolicyType(Action.View, PolicyType.QUALITY)
  ) {
    return Category.Quality;
  }
  return null;
};

type IssuesNavItemProps = {
  routerPush: InjectedRouter['push'];
  isOnGlobalIssues: boolean;
};

/**
 * The Issues nav dropdown item.
 *
 * @param {InjectedRouter['push']} routerPush - router.push
 * @param {boolean} isOnGlobalIssues -
 */
const IssuesNavItem: VoidFunctionComponent<IssuesNavItemProps> = ({ routerPush, isOnGlobalIssues }) => {
  const permittedIssueCategories = permittedIssueCategoriesToView();
  const isIssueOverviewEnabled = orgHasIssueOverviewEnabled();

  return (
    permittedIssueCategories.length > 0 && (
      <li className="nav-right link-dropdown-right" key="nav-link-issues">
        <Dropdown id="packages-dropdown" pullRight>
          <Dropdown.Toggle className={isOnGlobalIssues ? 'active' : ''} useAnchor />
          <Dropdown.Menu className="nav-issues-dropdown">
            <PaidFeatureGate
              isRestricted={isFreeTier({
                plan: window.user?.organization?.stripe_plan,
                stripePriceLimit: window.user?.organization?.stripePriceLimit,
              })}
              modalFocusFeature={issueOverviewFeature}
            >
              <MenuItemLink key="overview" eventKey="/issues/overview" routerPush={routerPush}>
                Overview
              </MenuItemLink>
            </PaidFeatureGate>
            {permittedIssueCategories.map((category) => (
              <MenuItemLink key={category} eventKey={`/issues/${category}`} routerPush={routerPush}>
                {getCategoryLabel(category as Category)}
              </MenuItemLink>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        <RouterLink
          className={isOnGlobalIssues ? 'active' : ''}
          activeClassName="active"
          to={isIssueOverviewEnabled ? '/issues/overview' : `/issues/${permittedIssueCategories[0]}`}
        >
          Issues
        </RouterLink>
      </li>
    )
  );
};

type AdminNavItemProps = {
  routerPush: InjectedRouter['push'];
  pathname: string;
};

/**
 * Dropdown item for all of the admin/super user nav items.
 */
const AdminNavItem: VoidFunctionComponent<AdminNavItemProps> = ({ routerPush, pathname }) => {
  if (!window.user) {
    return null;
  }
  const isSuper = window.user.super;
  const hasRawVulnsEnabled = isFeatureEnabled(RAW_VULN_EDIT);
  if (!isSuper && !hasRawVulnsEnabled) {
    return null;
  }

  //  Kirill's path, since he isn't a super user but has RawVulns access.
  if (hasRawVulnsEnabled && !isSuper) {
    return (
      <li className="nav-right">
        <RouterLink activeClassName="active" to="/raw-vulns">
          Vulns
        </RouterLink>
      </li>
    );
  }

  const isOnAdminPage = [
    pathname.startsWith('/admin'),
    pathname.startsWith('/internal-tools'),
    pathname.startsWith('/raw-vulns'),
  ].some(isOnPath => !!isOnPath);

  return (
    <li className="nav-right link-dropdown-right" key="nav-link-admin">
      {/* @ts-expect-error We have always done this, but the router was of type `any`. Now that the router is properly typed, it throws a type error. */}
      <Dropdown id="admin-nav-dropdown" pullRight>
        <Dropdown.Toggle className={isOnAdminPage ? 'active' : ''} useAnchor />
        <Dropdown.Menu className="nav-issues-dropdown">
          <MenuItemLink key="admin" eventKey="/admin" routerPush={routerPush}>
            Admin
          </MenuItemLink>
          <MenuItemLink key="internal-tools" eventKey="/internal-tools" routerPush={routerPush}>
            Internal Tools
          </MenuItemLink>
          {hasRawVulnsEnabled && (
            <MenuItemLink key="vulns" eventKey="/raw-vulns" routerPush={routerPush}>
              Vulns
            </MenuItemLink>
          )}
        </Dropdown.Menu>
      </Dropdown>
      <RouterLink className={isOnAdminPage ? 'active' : ''} activeClassName="active" to="/admin">
        Admin
      </RouterLink>
    </li>
  );
};

type NavigationProps = RouterProps & {
  routes?: any;
  router: InjectedRouter;
};

const Navigation: FunctionComponent<NavigationProps> = ({ params, location, routes, router }) => {
  const state = useSelector(NavigationStore.selectors.get);
  const Subnav = state.subnav;

  const doesPathIncludesProjects = location.pathname.includes('/projects/');
  const isOnProjects = location.pathname.match(projectsPagePath) || location.pathname.match(releaseGroupsPagePath);
  const isOnPackages = location.pathname.includes('/packages') && !doesPathIncludesProjects;
  const isOnGlobalIssues = location.pathname.startsWith('/issues') && !doesPathIncludesProjects;
  const defaultPoliciesPage = getDefaultPoliciesPage();

  return (
    <header
      className={cx('dashboard-navigation-wrapper', {
        //  When this is set (in `useCallToAction`), the nav will not render with a border-bottom (the CTA manages that instead).
        'dashboard-navigation-wrapper--cta': state.navProps?.shouldUseCallToAction,
      })}
    >
      <OrgAlertBanner />
      <AnnouncementBanner />
      {window.on_prem && isOnProjects && <BannerManager />}
      <div className={cx('dashboard-navigation container container-responsive nomargin-top', state.expand)}>
        <ul className="category-navigation">
          <li>
            {window.user ? (
              <RouterLink to="/" style={{ paddingTop: '3px' }}>
                <FOSSALogo width={72} />
              </RouterLink>
            ) : (
              <Link style={{ paddingTop: '3px' }} to="https://fossa.com" openInNewTab>
                <FOSSALogo width={72} /> <span className="badge label-knockout">Public Index</span>
              </Link>
            )}
          </li>
          <li className="nav-right nav-hover">
            <AccountDropdown user={window.user} router={router} />
          </li>
          <SupportDropdown />
          {defaultPoliciesPage && (
            <li className="nav-right" key="nav-link-policies">
              <RouterLink activeClassName="active" to={`/policies/${defaultPoliciesPage}`}>
                Policies
              </RouterLink>
            </li>
          )}
          {(userHasPermissionToCreateOrgReports() || userHasPermissionToCreateTeamReports()) && (
            <li className="nav-right" key="nav-link-components">
              <RouterLink activeClassName="active" to="/reports">
                Reports
              </RouterLink>
            </li>
          )}
          {isFeatureEnabled(PACKAGE_INVENTORY) && (
            <li className="nav-right" key="nav-link-packages">
              <RouterLink className={isOnPackages ? 'active' : ''} activeClassName="active" to="/packages">
                Packages
              </RouterLink>
            </li>
          )}
          <FeatureFlagRequired flagName={SBOM_PORTAL}>
            <PortalUrlOnlyGate>
              <SBOMPortalLinks pathname={location.pathname} routerPush={router.push} />
            </PortalUrlOnlyGate>
          </FeatureFlagRequired>
          <IssuesNavItem routerPush={router.push} isOnGlobalIssues={isOnGlobalIssues} />
          {window.user && (
            <li className="nav-right" key="nav-link-projects">
              <RouterLink activeClassName="active" to="/projects">
                Projects
              </RouterLink>
            </li>
          )}
          <AdminNavItem pathname={location.pathname} routerPush={router.push} />
        </ul>

        {Subnav && (
          // @ts-expect-error We can't know for sure what the props are going to be for the element, so this will always complain. We define the type as a React Component.
          <Subnav
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...state.navProps}
            expand={state.expand}
            params={params}
            routes={routes}
            location={location}
            router={router}
          />
        )}
      </div>
    </header>
  );
};

export default Navigation;
